// skipcq: JS-W1028, JS-0128
import React from 'react';
import { Link } from 'react-router-dom';
import {
  CardContent,
  CardActions,
  Button,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import DashboardSurfaceCard from './DashboardSurfaceCard';
import useSystemStats from '../../hooks/useSystemStats';
import useUserTrends from '../../hooks/useUserTrends';
import Sparkline from '../widgets/Sparkline';

function UserManagementCard() {
  const { systemStats, loading, error } = useSystemStats();
  const { trendData, loading: trendLoading } = useUserTrends();

  // Extract user count from metrics
  const uniqueUsersCount = systemStats?.metrics?.uniqueUsersCount || 0;
  const uniqueBeerReviewUsersCount =
    systemStats?.metrics?.uniqueBeerReviewUsersCount || 0;
  const uniqueCheckInUsersCount =
    systemStats?.metrics?.uniqueCheckInUsersCount || 0;

  // Extract trend values for sparklines
  const hospitalityTrendValues = trendData.map((item) => item.uniqueUsersCount);
  const beerTrendValues = trendData.map(
    (item) => item.uniqueBeerReviewUsersCount,
  );
  const checkinTrendValues = trendData.map(
    (item) => item.uniqueCheckInUsersCount,
  );

  // Helper function to calculate change metrics
  const calculateChange = (values) => {
    const firstValue = values[0] || 0;
    const lastValue = values[values.length - 1] || 0;
    const countChange = lastValue - firstValue;
    const percentChange = firstValue > 0 ? (countChange / firstValue) * 100 : 0;
    return { countChange, percentChange };
  };

  // Helper function to get trend icon
  const getTrendIcon = (change) => {
    if (change > 0) return TrendingUp;
    if (change < 0) return TrendingDown;
    return TrendingFlat;
  };

  // Helper function to get trend color
  const getTrendColor = (change) => {
    if (change > 0) return 'success.main';
    if (change < 0) return 'error.main';
    return 'text.secondary';
  };

  // Calculate changes for each metric
  const hospitalityChange = calculateChange(hospitalityTrendValues);
  const HospitalityTrendIcon = getTrendIcon(hospitalityChange.countChange);
  const beerChange = calculateChange(beerTrendValues);
  const BeerTrendIcon = getTrendIcon(beerChange.countChange);
  const checkinChange = calculateChange(checkinTrendValues);
  const CheckinTrendIcon = getTrendIcon(checkinChange.countChange);

  return (
    <DashboardSurfaceCard>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          User Statistics
        </Typography>
        <Typography variant="body2" paragraph sx={{
          color: "text.secondary"
        }}>
          Overview of user activity
        </Typography>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2
            }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error">
            Error loading stats
          </Typography>
        ) : (
          <>
            {/* Hospitality reviewers */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%"
              }}>
              <Typography
                variant="h4"
                component="div"
                color="primary"
                sx={{ mr: 2 }}
              >
                {uniqueUsersCount.toLocaleString()}
              </Typography>
              {!trendLoading && hospitalityTrendValues.length > 0 && (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Sparkline
                      data={hospitalityTrendValues}
                      width="100%"
                      height={24}
                      color="primary.main"
                      strokeWidth={2.5}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 1
                    }}>
                    <HospitalityTrendIcon
                      sx={{
                        color: getTrendColor(hospitalityChange.countChange),
                        fontSize: 16,
                        mr: 0.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: getTrendColor(hospitalityChange.countChange),
                          fontWeight: 'medium',
                          lineHeight: 1,
                        }}
                      >
                        {hospitalityChange.countChange > 0 ? '+' : ''}
                        {hospitalityChange.countChange.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: getTrendColor(hospitalityChange.countChange),
                          display: 'block',
                          lineHeight: 1,
                        }}
                      >
                        ({hospitalityChange.percentChange > 0 ? '+' : ''}
                        {hospitalityChange.percentChange.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Unique users who did a hospitality review
            </Typography>

            {/* Beer reviewers */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                mt: 1
              }}>
              <Typography
                variant="h5"
                component="div"
                color="secondary"
                sx={{ mr: 2 }}
              >
                {uniqueBeerReviewUsersCount.toLocaleString()}
              </Typography>
              {!trendLoading && beerTrendValues.length > 0 && (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Sparkline
                      data={beerTrendValues}
                      width="100%"
                      height={20}
                      color="secondary.main"
                      strokeWidth={2.5}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 1
                    }}>
                    <BeerTrendIcon
                      sx={{
                        color: getTrendColor(beerChange.countChange),
                        fontSize: 14,
                        mr: 0.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: getTrendColor(beerChange.countChange),
                          fontWeight: 'medium',
                          lineHeight: 1,
                        }}
                      >
                        {beerChange.countChange > 0 ? '+' : ''}
                        {beerChange.countChange.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: getTrendColor(beerChange.countChange),
                          display: 'block',
                          lineHeight: 1,
                        }}
                      >
                        ({beerChange.percentChange > 0 ? '+' : ''}
                        {beerChange.percentChange.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Unique users who did a beer review
            </Typography>

            {/* Check-in users */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                mt: 1
              }}>
              <Typography
                variant="h5"
                component="div"
                color="info"
                sx={{ mr: 2 }}
              >
                {uniqueCheckInUsersCount.toLocaleString()}
              </Typography>
              {!trendLoading && checkinTrendValues.length > 0 && (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Sparkline
                      data={checkinTrendValues}
                      width="100%"
                      height={20}
                      color="info.main"
                      strokeWidth={2.5}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 1
                    }}>
                    <CheckinTrendIcon
                      sx={{
                        color: getTrendColor(checkinChange.countChange),
                        fontSize: 14,
                        mr: 0.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: getTrendColor(checkinChange.countChange),
                          fontWeight: 'medium',
                          lineHeight: 1,
                        }}
                      >
                        {checkinChange.countChange > 0 ? '+' : ''}
                        {checkinChange.countChange.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: getTrendColor(checkinChange.countChange),
                          display: 'block',
                          lineHeight: 1,
                        }}
                      >
                        ({checkinChange.percentChange > 0 ? '+' : ''}
                        {checkinChange.percentChange.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Unique users who have logged activity
            </Typography>
          </>
        )}
      </CardContent>
      <CardActions>
        <Button variant="outlined" component={Link} to="/users" fullWidth>
          Manage Users
        </Button>
      </CardActions>
    </DashboardSurfaceCard>
  );
}

export default UserManagementCard;
